// =====================================================================
// src/lib/painel.js — o que as TVs decidem, sem DOM
//
// Puro. O banco (painel_tempo_real, 0019 → 0023) manda UM json a cada
// chamada: andares com quartos e estado, contadores, fila de inspeção,
// camareiras em atividade, chegadas/saídas do dia, os alertas e o
// movimento ao vivo. Aqui se decide como isso vira tela: a classe (cor)
// de cada quarto, os chips do alerta grande, os quatro medidores, a
// prioridade da governança, as chegadas por faixa da recepção, o detalhe
// de um quarto, quando a tela avisa que está velha e o ritmo do polling.
//
// Duas VISTAS da mesma função (decisão de 15/09/2026): `governanca` (a TV
// da governança: nunca vê nome) e `recepcao` (pode ver quem chega ao
// tocar num quarto — o nome vem de hospedes_do_quarto, que o banco só
// entrega a quem tem hóspedes: ver). A vista vem do hash.
// =====================================================================
import { ESTADOS } from './governanca.js';

export const INTERVALO_MS = 20_000;          // o combinado: a cada 20 s
export const INTERVALO_ESCONDIDA_MS = 60_000; // aba escondida: uma vez por minuto
export const VELHA_APOS_MS = 90_000;          // sem resposta há 90 s → a tela avisa
export const DETALHE_MS = 20_000;             // o detalhe de um quarto some sozinho em 20 s

export const VISTAS = Object.freeze(['governanca', 'recepcao']);

// As classes (cores) da grade. `a_limpar` se divide: saída/troca/entrada
// (o que decide o check-in) fica no vermelho forte; arrumação de quem fica
// ganha um tom mais suave (decisão de 15/09) — de manhã, tudo ocupado
// precisa de arrumação e a casa inteira ficaria vermelha.
export const ROTULO_CLASSE = Object.freeze({
  a_limpar: 'Aguardando camareira', arrumacao: 'Arrumação (hóspede fica)', em_limpeza: 'Em limpeza',
  aguardando_inspecao: 'Aguardando inspeção', liberado: 'Liberada', ocupado: 'Ocupada', livre: 'Livre',
  aguarda_checkout: 'Aguarda check-out',   // 0086: sai hoje e o hóspede ainda está dentro
  bloqueado: 'Bloqueado / Manutenção',
});
export const CLASSES_LEGENDA = Object.freeze(['a_limpar', 'arrumacao', 'em_limpeza', 'aguardando_inspecao', 'liberado', 'aguarda_checkout', 'livre', 'bloqueado']);
// 0067 (Hugo, 26/09/2026): quarto bloqueado por quem responde por ele (a
// imobiliária, o proprietário particular ou a administração do
// hotel) vence qualquer outro estado — ele não entra na fila da limpeza nem
// recebe hóspede. Na grade, cinza com um X; na legenda, "Bloqueado /
// Manutenção" (o rótulo do MdM). docs/bloqueio-de-quarto.md
export function classeDoQuarto(q) {
  if (!q) return 'livre';
  if (q.bloqueado) return 'bloqueado';
  if (q.estado === 'sem_movimento') return q.ocupado ? 'ocupado' : 'livre';
  if (q.estado === 'a_limpar' && q.tipo === 'arrumacao') return 'arrumacao';
  return ESTADOS.includes(q.estado) ? q.estado : 'livre';
}

export const ROTULO_TIPO = Object.freeze({ saida: 'saída', troca: 'troca', entrada: 'entrada', arrumacao: 'arrumação', avulsa: 'avulsa' });

export const ALERTAS = Object.freeze({
  chegada_sem_quarto: { rotulo: 'Chegada', classe: 'al-vermelho', texto: a => `UH ${a.numero} — hóspede chegando sem quarto liberado` },
  inspecao_atrasada:  { rotulo: 'Inspeção', classe: 'al-azul',     texto: a => `UH ${a.numero} limpa por ${primeiroNome(a.quem) || 'camareira'}, esperando inspeção` },
  saida_atrasada:     { rotulo: 'Saída',    classe: 'al-amarelo',  texto: a => `UH ${a.numero} — sai hoje e ainda está ocupada` },
  chegada_cedo:       { rotulo: 'Cedo',     classe: 'al-laranja',  texto: a => `UH ${a.numero} chega cedo e não está pronta` },
  // 0067: bloqueada com reserva no dia — a recepção vê; quem resolve é quem
  // responde pelo quarto ou quem trouxe a reserva
  quarto_bloqueado:   { rotulo: 'Bloqueio', classe: 'al-vermelho', texto: a => `UH ${a.numero} bloqueada com reserva para hoje` },
});

// A faixa de chegada que o hóspede informou (0021).
export const FAIXA_CURTA = Object.freeze({ antes_12: '<12h', '12_15': '12–15h', '15_18': '15–18h', depois_18: '>18h' });
export const FAIXA_LONGA = Object.freeze({ antes_12: 'antes das 12h', '12_15': '12h–15h', '15_18': '15h–18h', depois_18: 'depois das 18h' });
export const FAIXAS = Object.freeze(['antes_12', '12_15', '15_18', 'depois_18']);
export const faixaCurta = f => FAIXA_CURTA[f] || '';
const ordemDaFaixa = f => { const i = FAIXAS.indexOf(f); return i < 0 ? FAIXAS.length : i; };

// O json do banco vem sempre inteiro; a tela nunca recebe "metade". Se o
// formato mudar, é aqui que quebra — de propósito.
export function validarPainel(j) {
  const erros = [];
  if (!j || typeof j !== 'object') return ['sem dados'];
  for (const k of ['andares', 'fila_inspecao', 'camareiras', 'alertas', 'movimento']) if (!Array.isArray(j[k])) erros.push(`${k} não é lista`);
  for (const k of ['contadores', 'chegadas', 'saidas']) if (!j[k] || typeof j[k] !== 'object') erros.push(`${k} ausente`);
  return erros;
}

// Índices do json, calculados uma vez por desenho.
export function indices(j) {
  const quartos = [], porNumero = new Map(), chegadas = new Map(), saidas = new Map(), alertas = new Map();
  for (const a of j.andares || []) for (const q of a.quartos || []) { quartos.push({ ...q, andar: a.andar }); porNumero.set(q.numero, quartos[quartos.length - 1]); }
  for (const c of (j.chegadas && j.chegadas.lista) || []) chegadas.set(c.numero, c);
  for (const s of (j.saidas && j.saidas.lista) || []) saidas.set(s.numero, s);
  for (const a of j.alertas || []) { if (!alertas.has(a.numero)) alertas.set(a.numero, []); alertas.get(a.numero).push(a); }
  return { quartos, porNumero, chegadas, saidas, alertas };
}

// As marcas de uma célula: chega hoje (ponto), já chegou (ponto verde),
// chega cedo (borda laranja), alerta que não seja "cedo" (borda vermelha).
export function marcasDoQuarto(q, ix) {
  const m = [];
  const c = ix.chegadas.get(q.numero);
  if (q.chega_hoje) m.push('chega');
  if (c && c.chegou) m.push('chegou');
  if (q.chega_cedo) m.push('cedo');
  if ((ix.alertas.get(q.numero) || []).some(a => a.tipo !== 'chegada_cedo')) m.push('urgente');
  return m;
}

// O resumo do andar, nas quatro caixinhas à direita da linha.
export const CLASSES_RESUMO = Object.freeze(['a_limpar', 'em_limpeza', 'aguardando_inspecao', 'liberado']);
export function resumoDoAndar(quartos) {
  const r = {}; for (const c of CLASSES_RESUMO) r[c] = 0;
  for (const q of quartos || []) { const c = classeDoQuarto(q); if (c in r) r[c]++; }
  return r;
}
// Quantos quartos tem o andar mais cheio: as colunas da grade, para as
// linhas alinharem (o Garvey tem 38; o Monteiro, 27 num andar só).
export function colunasDaGrade(andares) {
  return Math.max(1, ...(andares || []).map(a => (a.quartos || []).length));
}
export function contagemPorClasse(quartos) {
  const r = {}; for (const q of quartos || []) { const c = classeDoQuarto(q); r[c] = (r[c] || 0) + 1; }
  return r;
}

// O alerta grande: as chegadas de hoje que ainda não têm quarto liberado.
export function chegadasSemQuarto(j) {
  return ((j.chegadas && j.chegadas.lista) || []).filter(c => !c.chegou && c.estado !== 'liberado');
}
// Os chips do alerta: a governança lê por estado da limpeza; a recepção,
// pela hora em que o hóspede disse que chega.
export function chipsDoAlerta(j, vista) {
  const sem = chegadasSemQuarto(j);
  if (vista === 'recepcao') {
    return [...FAIXAS, null].map(f => ({ chave: f || 'sem_hora', rotulo: f ? `chega ${FAIXA_LONGA[f]}` : 'sem hora informada', n: sem.filter(c => (c.chegada_prevista || null) === f).length })).filter(x => x.n > 0);
  }
  return ['a_limpar', 'em_limpeza', 'aguardando_inspecao'].map(e => ({ chave: e, rotulo: ROTULO_CLASSE[e], n: sem.filter(c => c.estado === e).length }));
}
export function textoDoAlerta(n) {
  return n === 0 ? 'Todas as chegadas de hoje têm quarto liberado' : 'chegadas de hoje ainda sem quarto liberado';
}

// Os quatro anéis. Só o que a função devolve — sem "meta" inventada.
export function medidores(j) {
  const c = j.contadores || {}, ch = j.chegadas || {}, s = j.saidas || {};
  const n = k => Number(c[k] || 0);
  const comMovimento = n('a_limpar') + n('em_limpeza') + n('aguardando_inspecao') + n('liberado');
  const pct = (a, b) => (b ? Math.max(0, Math.min(1, a / b)) : 0);
  const plural = (k, um, muitos) => `${k} ${k === 1 ? um : muitos}`;
  return [
    { chave: 'ocupacao', v: pct(n('ocupados'), n('quartos')), rotulo: 'Ocupação agora', meta: `${n('ocupados')} de ${n('quartos')} UHs ocupadas` },
    { chave: 'limpeza', v: pct(n('liberado'), comMovimento), rotulo: 'Limpeza do dia', meta: `${n('liberado')} de ${comMovimento} liberadas` },
    { chave: 'chegadas', v: pct(ch.chegaram || 0, ch.previstas || 0), rotulo: 'Chegadas do dia', meta: `${ch.chegaram || 0} de ${ch.previstas || 0} chegaram` + ((ch.cedo || 0) ? ` · ${plural(ch.cedo, 'chega cedo', 'chegam cedo')}` : '') },
    { chave: 'saidas', v: pct(s.sairam || 0, s.previstas || 0), rotulo: 'Saídas do dia', meta: `${s.sairam || 0} de ${s.previstas || 0} saíram` + ((s.pendentes || 0) ? ` · ${s.pendentes} ainda dentro` : '') },
  ].map(m => ({ ...m, pct: Math.round(m.v * 100) }));
}

// A prioridade da governança: o que atribuir (a limpar com chegada hoje,
// pela faixa), o que anda (em limpeza, mais demorado primeiro; lento >45
// min) e o que conferir (esperando inspeção; lento a partir de 15 min).
export const LENTO_LIMPEZA_MIN = 45;
export const LENTO_INSPECAO_MIN = 15;
export const MAX_POR_LINHA = 14;
export function prioridadeGovernanca(j, ix = indices(j)) {
  const porNumero = (a, b) => String(a.numero).localeCompare(String(b.numero), undefined, { numeric: true });
  const atribuir = ix.quartos.filter(q => classeDoQuarto(q) === 'a_limpar' && q.chega_hoje)
    .sort((a, b) => ordemDaFaixa(a.chegada_prevista) - ordemDaFaixa(b.chegada_prevista) || porNumero(a, b));
  const curso = ix.quartos.filter(q => q.estado === 'em_limpeza').sort((a, b) => (b.minutos || 0) - (a.minutos || 0) || porNumero(a, b));
  const conferir = ix.quartos.filter(q => q.estado === 'aguardando_inspecao').sort((a, b) => (b.minutos || 0) - (a.minutos || 0) || porNumero(a, b));
  return [
    { chave: 'a_limpar', rotulo: 'Atribuir', itens: atribuir.map(q => ({ numero: q.numero, extra: faixaCurta(q.chegada_prevista), lento: false })) },
    { chave: 'em_limpeza', rotulo: 'Em curso', itens: curso.map(q => ({ numero: q.numero, extra: `${q.minutos ?? 0}′`, lento: (q.minutos || 0) > LENTO_LIMPEZA_MIN })) },
    { chave: 'aguardando_inspecao', rotulo: 'Conferir', itens: conferir.map(q => ({ numero: q.numero, extra: `${q.minutos ?? 0}′`, lento: (q.minutos || 0) >= LENTO_INSPECAO_MIN })) },
  ].map(l => ({ ...l, n: l.itens.length, mostrar: l.itens.slice(0, MAX_POR_LINHA), resto: Math.max(0, l.itens.length - MAX_POR_LINHA) }));
}

// As chegadas da recepção, por hora prevista: quem ainda vem, com o quarto
// pronto (verde) ou não (vermelho), e quem já chegou.
export function chegadasPorFaixa(j) {
  const lista = (j.chegadas && j.chegadas.lista) || [];
  const porNumero = (a, b) => String(a.numero).localeCompare(String(b.numero), undefined, { numeric: true });
  const linhas = [...FAIXAS, null].map(f => ({
    chave: f || 'sem_hora', rotulo: f ? FAIXA_LONGA[f] : 'sem hora',
    itens: lista.filter(c => !c.chegou && (c.chegada_prevista || null) === f)
      .sort((a, b) => (a.estado === 'liberado') - (b.estado === 'liberado') || porNumero(a, b))
      .map(c => ({ numero: c.numero, pronto: c.estado === 'liberado' })),
  }));
  linhas.push({ chave: 'chegaram', rotulo: 'Já chegaram', itens: lista.filter(c => c.chegou).sort(porNumero).map(c => ({ numero: c.numero, pronto: true })) });
  return linhas.map(l => ({ ...l, n: l.itens.length, mostrar: l.itens.slice(0, MAX_POR_LINHA), resto: Math.max(0, l.itens.length - MAX_POR_LINHA) }));
}

// O detalhe de um quarto (sem o nome — quem chama decide se pede ao banco).
export function detalheDoQuarto(j, numero, ix = indices(j)) {
  const q = ix.porNumero.get(numero);
  if (!q) return null;
  const classe = classeDoQuarto(q), c = ix.chegadas.get(numero), s = ix.saidas.get(numero), al = ix.alertas.get(numero) || [];
  const linhas = [];
  linhas.push(['Andar', q.andar === null || q.andar === undefined ? 'sem andar' : `${q.andar}º`]);
  linhas.push(['Pessoas', String(q.pessoas ?? 0)]);
  if (q.estado === 'em_limpeza') { linhas.push(['Camareira', q.camareira || '—']); linhas.push(['Em curso há', `${q.minutos ?? 0} min`]); }
  else if (q.estado === 'aguardando_inspecao') { linhas.push(['Limpou', q.camareira || '—']); linhas.push(['Esperando inspeção há', `${q.minutos ?? 0} min`]); }
  else if (q.estado === 'liberado' && q.camareira) linhas.push(['Limpou', q.camareira]);
  else if (q.estado === 'a_limpar') linhas.push(['Camareira', 'ainda não atribuída', true]);
  if (q.tipo) linhas.push(['Movimento', ROTULO_TIPO[q.tipo] || q.tipo]);
  if (c) linhas.push(['Chegada', c.chegou ? 'já chegou' : `hoje${c.chegada_prevista ? ', ' + FAIXA_LONGA[c.chegada_prevista] : ' (hora não informada)'}${c.cedo ? ' · cedo' : ''}`]);
  if (s) linhas.push(['Saída', s.saiu ? 'já saiu' : `hoje, até ${j.hora_checkout || ''}`.trim()]);
  if (al.length) linhas.push(['Alerta', al.map(a => (ALERTAS[a.tipo] ? ALERTAS[a.tipo].texto(a) : `UH ${a.numero}`) + (a.minutos ? ` há ${a.minutos} min` : '')).join(' · ')]);
  return { numero, classe, rotulo: ROTULO_CLASSE[classe], linhas, temHospede: !!(q.ocupado || c) };
}

// A lista de nomes que hospedes_do_quarto devolve, em linhas de tela.
export function linhasDeHospedes(lista) {
  return (lista || []).map(h => ({
    nome: h.nome, nota: [h.principal ? '' : 'acompanhante', h.chegou ? '' : 'ainda não chegou'].filter(Boolean).join(' · '),
  }));
}

// O movimento ao vivo: hora, texto, duração.
export function vestirMovimento(mov) {
  const T = {
    limpeza_iniciada: m => `${primeiroNome(m.quem) || 'Camareira'} iniciou a UH ${m.numero}`,
    limpeza_terminada: m => `${primeiroNome(m.quem) || 'Camareira'} terminou a UH ${m.numero}`,
    quarto_liberado: m => `UH ${m.numero} liberada${m.quem ? ` por ${primeiroNome(m.quem)}` : ''}`,
    limpeza_devolvida: m => `UH ${m.numero} devolvida à limpeza${m.quem ? ` por ${primeiroNome(m.quem)}` : ''}`,
    checkin: m => `Check-in na UH ${m.numero}`,
    checkout: m => `Check-out da UH ${m.numero}`,
  };
  return (mov || []).map(m => ({ hora: m.hora || '', texto: (T[m.tipo] || (x => `UH ${x.numero}`))(m), dur: m.minutos ? `${m.minutos} min` : '' }));
}

// Os alertas já vêm ordenados do banco. Aqui só se veste: rótulo, classe,
// texto. Tipo desconhecido não derruba a tela — vira "Atenção".
export function vestirAlertas(alertas) {
  return (alertas || []).map(a => {
    const d = ALERTAS[a.tipo] || { rotulo: 'Atenção', classe: 'al-cinza', texto: x => `UH ${x.numero}` };
    return { ...a, rotulo: d.rotulo, classe: d.classe, texto: d.texto(a), dur: a.minutos ? `${a.minutos} min` : '' };
  });
}
export function primeiroNome(nome) { return String(nome || '').trim().split(/\s+/)[0] || ''; }

// A tela está velha? (última resposta boa há mais de VELHA_APOS_MS)
export function estaVelha(ultimaRespostaMs, agoraMs) {
  if (!ultimaRespostaMs) return false;
  return agoraMs - ultimaRespostaMs > VELHA_APOS_MS;
}

// O próximo intervalo do polling: 20 s normal; 60 s com a aba escondida;
// depois de falhas consecutivas, recua (20 → 40 → 80 → … até 5 min).
export function proximoIntervalo({ escondida = false, falhas = 0 } = {}) {
  const base = escondida ? INTERVALO_ESCONDIDA_MS : INTERVALO_MS;
  if (!falhas) return base;
  return Math.min(base * Math.pow(2, Math.min(falhas, 4)), 300_000);
}

// A hora que aparece no canto: a do servidor no fuso do hotel (hora_local),
// não a do aparelho da TV — que pode estar em outro fuso ou desacertado.
export function rotuloDaHora(j) {
  return j && j.hora_local ? j.hora_local : '';
}
// "Ter · 15 set", da data do hotel (j.dia, 'AAAA-MM-DD').
export function rotuloDoDia(j) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(j && j.dia || '');
  if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return `${['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.getUTCDay()]} · ${d.getUTCDate()} ${['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][d.getUTCMonth()]}`;
}

// #painel?vista=recepcao&tv=1 → a vista e o modo TV. Só a presença conta;
// vista desconhecida cai na padrão. A padrão vem de quem está logado
// (vistaPadrao): quem vê hóspedes (a recepção, no computador dela — decisão
// de 15/09/2026: não há TV na recepção) abre na vista da recepção; quem não
// vê (a TV, a supervisão, a administração) abre na da governança.
export function parametrosDoHash(hash, padrao = 'governanca') {
  const q = String(hash || '').split('?')[1] || '';
  const p = new URLSearchParams(q);
  return { tv: p.get('tv') === '1', vista: VISTAS.includes(p.get('vista')) ? p.get('vista') : (VISTAS.includes(padrao) ? padrao : 'governanca') };
}
export function vistaPadrao(contexto) {
  if (contexto && contexto.usuario && contexto.usuario.sistema) return 'governanca';
  const n = ((contexto && contexto.modulos) || []).find(m => m && m.chave === 'hospedes');
  return n && (n.nivel === 'ver' || n.nivel === 'editar') ? 'recepcao' : 'governanca';
}
export function modoTvDoHash(hash) { return parametrosDoHash(hash).tv; }
export function vistaDoHash(hash, padrao) { return parametrosDoHash(hash, padrao).vista; }
export function hashDoPainel({ tv = false, vista = 'governanca' } = {}) {
  const p = [];
  if (vista && vista !== 'governanca') p.push('vista=' + vista);
  if (tv) p.push('tv=1');
  return '#painel' + (p.length ? '?' + p.join('&') : '');
}
export function hashModoTv(ligado, vista = 'governanca') { return hashDoPainel({ tv: ligado, vista }); }

// As listas de tela têm limite: os primeiros N e "+ X".
export const MAX_ALERTAS = 4;
export const MAX_MOVIMENTO = 8;
export const MAX_SAIDAS = 40;
export function limitar(lista, max) {
  const l = lista || [];
  return { itens: l.slice(0, max), resto: Math.max(0, l.length - max) };
}
